import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  BookOpen, Download, Mail, Loader2, 
  CheckCircle, Library, ExternalLink, Calendar,
  RefreshCw
} from 'lucide-react'

interface PurchasedItem {
  slug: string
  title: string
  purchaseDate: string
  amount: number
  type: 'manual' | 'toolkit' | 'bundle'
}

export default function MyLibrary() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [purchases, setPurchases] = useState<PurchasedItem[]>([])
  const [error, setError] = useState('')

  // Check if sessionToken is stored in localStorage
  useEffect(() => {
    const sessionToken = localStorage.getItem('sessionToken')
    const userName = localStorage.getItem('userName')

    if (sessionToken && userName) {
      setEmail(userName)
      setIsVerified(true)
      // Auto-fetch purchases with session token
      fetchPurchasesWithToken(sessionToken)
    }
  }, [])

  const fetchPurchasesWithToken = async (token: string) => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(`/.netlify/functions/user-dashboard?token=${token}`)
      const data = await response.json()

      if (data.success && data.purchases) {
        setPurchases(data.purchases)
        setIsVerified(true)
      } else {
        setError(data.error || 'Failed to load purchases')
        setIsVerified(false)
      }
    } catch (err) {
      setError('Failed to fetch purchases. Please try again.')
      setIsVerified(false)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPurchases = async (emailToCheck: string, passwordToCheck: string) => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/.netlify/functions/auth-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToCheck, password: passwordToCheck, register: false })
      })

      const data = await response.json()

      if (data.success && data.sessionToken) {
        setEmail(data.user.email)
        setPurchases([]) // Will be fetched next
        setIsVerified(true)
        localStorage.setItem('sessionToken', data.sessionToken)
        localStorage.setItem('userName', data.user.name)
        // Fetch purchases with token
        fetchPurchasesWithToken(data.sessionToken)
      } else {
        setError(data.error || 'Invalid email or password')
        setIsVerified(false)
      }
    } catch (err) {
      setError('Failed to authenticate. Please try again.')
      setIsVerified(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email.trim() && password.trim()) {
      fetchPurchases(email.trim(), password.trim())
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('sessionToken')
    localStorage.removeItem('userName')
    setIsVerified(false)
    setPurchases([])
    setEmail('')
    setPassword('')
  }

  const handleDownload = async (item: PurchasedItem) => {
    try {
      const sessionToken = localStorage.getItem('sessionToken')
      if (!sessionToken) {
        alert('Your session has expired. Please login again.')
        return
      }

      const response = await fetch('/.netlify/functions/download-manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken,
          manualSlug: item.slug
        })
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${item.slug}-rondanini.html`
        a.click()
        window.URL.revokeObjectURL(url)
      } else {
        const errorData = await response.json()
        alert(errorData.message || 'Download failed. Please try again.')
      }
    } catch (error) {
      console.error('Download error:', error)
      alert('Download failed. Please try again.')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy-800 to-navy-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Library className="w-16 h-16 mx-auto mb-4 text-gold-400" />
          <h1 className="text-4xl font-serif font-bold mb-4">My Library</h1>
          <p className="text-xl text-gray-300">
            Access your library — subscriptions, full-library purchase, and eligible legacy entitlements
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <AnimatePresence mode="wait">
          {!isVerified ? (
            /* Email Verification Form */
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md mx-auto"
            >
              <div className="text-center mb-8">
                <Mail className="w-12 h-12 mx-auto mb-4 text-navy-600 dark:text-navy-400" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Sign in to your library
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Use the email from your Stripe checkout or account registration
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-navy-500 dark:focus:border-navy-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-navy-500 dark:focus:border-navy-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-navy-600 hover:bg-navy-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Access My Library
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 space-y-3 text-center text-sm text-gray-500 dark:text-gray-400">
                <p>
                  <Link to="/reset-password" className="text-navy-600 dark:text-navy-400 font-medium hover:underline">
                    Forgot your password?
                  </Link>
                </p>
                <p>Don't have an account?</p>
                <Link to="/subscribe" className="text-navy-600 dark:text-navy-400 font-medium hover:underline">
                  Get library access →
                </Link>
              </div>
            </motion.div>
          ) : (
            /* Library View */
            <motion.div
              key="library"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Update Notification Banner */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/40">
                      <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-900 dark:text-green-200 mb-2">
                      Your access stays current
                    </h3>
                    <p className="text-sm text-green-800 dark:text-green-300 mb-2">
                      We improve minimanuals over time. While you have an active subscription or a full-library purchase,
                      you get those updates in the browser. Download availability depends on your product terms.
                    </p>
                    <div className="text-xs text-green-700 dark:text-green-400 space-y-1">
                      <p>✓ <strong>Read online:</strong> Open any entitled minimanual from this list</p>
                      <p>✓ <strong>Subscription:</strong> Access lasts for the paid period; renew to continue</p>
                      <p>✓ <strong>One-time library:</strong> Entitlement continues per your purchase terms</p>
                      <p>✓ <strong>Support:</strong> Use the same email you used at checkout</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Info Bar */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 mb-8 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Logged in as</div>
                    <div className="font-medium text-gray-900 dark:text-white">{email}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('sessionToken')
                      if (token) fetchPurchasesWithToken(token)
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Refresh"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                  <Link
                    to="/reset-password"
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-3 py-1"
                  >
                    Change Password
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-3 py-1"
                  >
                    Sign out
                  </button>
                </div>
              </div>

              {/* Purchases List */}
              {purchases.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center">
                  <Library className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    No entitlements found yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    Get the full library on the subscription or one-time checkout page, then sign in here with the same
                    email.
                  </p>
                  <Link
                    to="/subscribe"
                    className="inline-flex items-center gap-2 bg-navy-600 hover:bg-navy-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                  >
                    View plans & checkout
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Your content ({purchases.length})
                    </h2>
                  </div>

                  {purchases.map((item, index) => (
                    <motion.div
                      key={item.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(item.purchaseDate).toLocaleDateString()}
                          </span>
                          <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                            Entitled
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <Link
                          to={`/${item.slug}`}
                          className="flex items-center gap-2 px-4 py-2 bg-navy-600 hover:bg-navy-700 text-white rounded-lg font-medium transition-colors"
                        >
                          <BookOpen className="w-4 h-4" />
                          Read Online
                        </Link>
                        <button
                          onClick={() => handleDownload(item)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Quick Links */}
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link
                  to="/series/all"
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 flex items-center gap-4 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">Browse the catalogue</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Explore series and free samples</div>
                  </div>
                </Link>
                
                <Link
                  to="/toolkit"
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 flex items-center gap-4 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                    <Download className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">Cash FX Toolkit</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Download Python toolkit</div>
                  </div>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
